/**
 * @module lib/recommended/render-helper
 */
import tgamConfig from "config/tgam";
import { getProp } from "utils/utils";
import find from "lodash/find";

/**
 * @method getChannel
 * @description gets all the data to render the section name at the top of the feature and link to the section page
 * @param {String} channel
 * @returns {Object} sectionData
 */
function getChannel(channel) {
  let channels = [
    { channel: "canada", path: "/canada/", title: "Canada" },
    { channel: "world", path: "/world/", title: "World" },
    { channel: "business", path: "/business/", title: "Report on Business" },
    { channel: "investing", path: "/investing/", title: "Investing" },
    { channel: "opinion", path: "/opinion/", title: "Opinion" },
    { channel: "politics", path: "/politics/", title: "Politics" },
    { channel: "sports", path: "/sports/", title: "Sports" },
    { channel: "life", path: "/life/", title: "Life" },
    { channel: "arts", path: "/arts/", title: "Arts" },
    { channel: "drive", path: "/drive/", title: "Globe Drive" },
    { channel: "globerealestate", path: "/real-estate/", title: "Real Estate" },
    { channel: "investor", path: "/investing/", title: "Investing" },
    { channel: "technology", path: "/business/technology/", title: "Technology" },
    { channel: "news", path: "/canada/", title: "Canada" },
    { channel: "globeandmail", path: "/canada/", title: "Canada" }
  ];

  return find(channels, (c) => c.channel === channel);
}

/**
 * @method getHeading
 * @description gets the top heading for the feature
 * @param {String} title
 * @param {String} style
 * @returns {String}
 */
export function getHeading(title, style) {
  let heading = `<h2 class="c-article-list__heading">${title}</h2>`;

  if (style === "relatedToThisStory") {
    heading = `<h2 class="c-related-articles__heading">${title}</h2>`;
  } else if (style === "listWithImages") {
    let { title, path } = getChannel(tgamConfig.datalayer.page.channel);
    let url = `${tgamConfig.env.baseCanonicalUrl}${path}`;
    heading =
      `<a href="${url}" class="c-article-list__label"><span>More from ${title}</span>
        <svg class="c-article-list__label-link__icon" role="img" pointer-events="none" focusable="false" aria-hidden="true">
          <use xlink:href="${tgamConfig.env.svgSpritePath}#package-label-link"></use>
        </svg>
      </a>`;
  } else if (style === "articleListRow") {
    heading = `<h2 class="c-article-list-row__heading">${title}</h2>`;
  }
  return title ? heading : "";
}

/**
 * @method getImage
 * @description get all the data to display the image
 * @param {Object} article
 * @param {String} label - story label
 * @returns {Object} image data
 */
export function getImage(article, label) {
  let path = null;
  const isOpinion = label === "opinion" || article.content_type === "column";

  // default image `alt` text when caption missing,
  // see: https://www.w3.org/WAI/tutorials/images/decorative/#decorative-image-as-part-of-a-text-link
  const defautAlt = "";

  // author_rel
  path = getProp(article, "author_rel[0].url220");
  if (isOpinion && path) {
    return { path, alt: getProp(article, "author_rel[0].caption", defautAlt) };
  }

  // promo_image
  path = getProp(article, "promo_image.urls['220']");
  if (path) {
    return { path, alt: getProp(article, "promo_image.caption", defautAlt) };
  }

  // picture_rel
  path = getProp(article, "picture_rel[0].url220");
  if (path) {
    return { path, alt: getProp(article, "picture_rel[0].caption", defautAlt) };
  }

  // image_url_return
  path = getProp(article, "image_url_return[0].w620");
  if (path) {
    return {
      path: `https://www.theglobeandmail.com/ece-images${path}`,
      alt: getProp(article, "article.picture_rel[0].alttext", defautAlt)
    };
  }
  return { path: null, alt: null };
}

/**
 * @method getLabel
 * @description Get the label data if exists
 * @param {Object} article
 * @returns {Object} final value for the label
 */
export function getLabel(article) {
  let label = {
    text: null,
    source: null
  };
  if (article.section_label !== undefined && article.section_label !== "") {
    label.source = "siteServiceLabel";
    label.text = article.section_label.toLowerCase();
    label = getLabelBgColour(label);
  }
  if (article.label !== undefined && article.label !== "" && label.text == null) {
    label.source = "storyBasicLabel";
    label.text = article.label.toLowerCase();
    label = getLabelBgColour(label);
  }
  return label;
}

/**
 * @method getLabelBgColour
 * @description Filter out the label text and get modifier classes (if exists)
 * @param {Object} c - collection of properties
 * @param {String} c.text
 * @param {String} c.source
 * @returns {Object}
 */
function getLabelBgColour({ text, source }) {
  let labelData = {
    text: null,
    colour: "",
  };

  // basic label class for labels with bg colours
  const classLabel = " c-card__label--";

  // label colours
  const opinionColour = "red700";
  const reviewColour = "gold900";
  const franchiseColour = "rust900";
  const breakingColour = "red500";
  const updateColour = "stone999";

  if (source === "siteServiceLabel") {
    if (["opinion", "analysis"].indexOf(text) >= 0) {
      labelData = { text, colour: classLabel + opinionColour };

    } else if (text.indexOf("review") >= 0) {
      labelData = { text, colour: classLabel + reviewColour };

    } else if (["streetwise", "facts & arguments", "rob magazine"].indexOf(text) >= 0) {
      labelData = { text, colour: classLabel + franchiseColour };
    }

  } else if (source === "storyBasicLabel") {
    if (["new", "breaking", "breaking news", "live discussion", "live now", "developing"].indexOf(text) >= 0) {
      labelData = { text, colour: classLabel + breakingColour };

    } else if (["earlier", "updated", "upcoming", "coming"].indexOf(text) >= 0) {
      labelData = { text, colour: classLabel + updateColour };

    } else if (["exclusive", "investigation", "in depth", "explainer"].indexOf(text) >= 0) {
      labelData = { text, colour: classLabel + franchiseColour };

    } else if (["opinion", "analysis"].indexOf(text) >= 0) {
      labelData = { text, colour: classLabel + opinionColour };
    }
  }
  return labelData;
}

/**
 * @method renderSubscribedIcon
 * @description renders the subscriber icon
 * @returns {String}
 */
export function renderSubscribedIcon() {
  return `
  <span class="c-indicator-icon c-indicator-icon--key" aria-label="Subscriber content">
    <span class="u-visually-hidden">Subscriber content</span>
    <svg class="c-indicator-icon__art c-indicator-icon__art--key" role="img" pointer-events="none" focusable="false" aria-hidden="true">
      <use xlink:href="${tgamConfig.env.svgSpritePath}#icon-key"></use>
    </svg>
  </span>
  `;
}

/**
 * @method renderFinalImages
 * @description renders the final version of images that later will be used by the main render function
 * @param {Object} data
 * @param {Object} displayData
 * @returns {String} html markup for the image
 */
export function renderFinalImages(data, displayData) {
  let img = "";
  if ((["listWithImages", "relatedToThisStory", "articleListRow"].indexOf(data.style) >= 0) && displayData.photo.path) {
    img =
      `
      <div class="c-article-list__img-wrapper">
        <div class="c-image-container c-image-container--lazy" style="padding-bottom: 66.67%">
          <div class="c-image-container--lazy__wrapper" style="background: #FFF">
            <img class="c-image"
              src="${displayData.photo.path}"
              alt="${displayData.photo.alt}">
          </div>
        </div>
      </div>
      `;
  }
  return img;
}

/**
 * @method renderFinalLabels
 * @description renders the final version of labels that later will be used by the main render function
 * @param {Object} data
 * @param {Object} displayData
 * @returns {String} html markup for the label
 */
export function renderFinalLabels(data, displayData) {
  let label = "";
  if (displayData.label.text) {
    label =
      `
      <span class='c-card__label ${data.style == "orderedList" ? "c-card__label--muted" : ""} ${data.style === "aorderedList" ? "" : displayData.label.colour}'>
        ${displayData.label.text}
      </span>
      `;
  }
  return label;
}

/**
 * @method renderFinalByLine
 * @description renders the final version of byline that later will be used by the main render function
 * @param {Object} displayData
 * @returns {String} html markup for the byline
 */
export function renderFinalByLine(displayData) {
  let byline = "";
  if ((displayData.label.text === "opinion" || displayData.contentType === "column") && displayData.byline) {
    byline =
      `
      <span class="c-card__meta">
        <span class="c-card__meta-authors">
          <span class="c-card__meta-author">${displayData.byline}</span>
        </span>
      </span>
      `;
  }
  return byline;
}
